import type { FC, FormEvent } from 'react';
import React, { useCallback } from 'react';
import { MDText } from 'i18n-react';

import { Badge } from '@motorway/mw-highway-code';
import { Input, Toggle } from '@motorway/mw-highway-code/alpha';

import texts from 'Components/VehicleList/VehicleListV2/VehicleListV2.texts.json';

import type { NameError } from '../validateSavedSearchName';

import { formatBadgeText } from './formatBadgeText';

import formStyles from '../SavedSearchesFlow.module.scss';

const LocalT = new MDText(texts.savedSearchModals);

interface EditViewProps {
	editName: string;
	isSendEmailAlert: boolean;
	nameError?: Nullable<NameError>;
	onNameChange: (value: string) => void;
	onSubmit: () => void;
	onToggleEmailAlert: () => void;
	savedFilterValues?: Array<{ key: string; value: string }>;
}

export const EditView: FC<EditViewProps> = ({
	editName,
	isSendEmailAlert,
	nameError,
	onNameChange,
	onSubmit,
	onToggleEmailAlert,
	savedFilterValues,
}) => {
	const handleSubmit = useCallback(
		(e: FormEvent<HTMLFormElement>) => {
			e.preventDefault();
			onSubmit();
		},
		[onSubmit],
	);

	return (
		<div className={formStyles.formContent}>
			<form id="edit-saved-search-form" onSubmit={handleSubmit}>
				<div className={formStyles.field}>
					<label className={formStyles.label} htmlFor="edit-saved-search-name">
						{`${LocalT.translate('name')}`}
					</label>
					<Input
						fullWidth
						formProps={{
							input: {
								onChange: (e: React.ChangeEvent<HTMLInputElement>) => onNameChange(e.target.value),
								type: 'text',
								value: editName,
							},
						}}
						id="edit-saved-search-name"
						intent={nameError ? 'error' : undefined}
						label={`${LocalT.translate('name')}`}
						placeholder={`${LocalT.translate('namePlaceholder')}`}
						showLabel={false}
						validationMessage={nameError ? `${LocalT.translate(nameError)}` : undefined}
					/>
				</div>
			</form>

			{savedFilterValues && savedFilterValues.length > 0 && (
				<div className={formStyles.field}>
					<span className={formStyles.label}>{`${LocalT.translate('criteria')}`}</span>
					<div className={formStyles.badges}>
						{savedFilterValues.map(({ key, value }) => (
							<Badge key={key} text={formatBadgeText(value)} variant="primary" />
						))}
					</div>
				</div>
			)}

			<div className={formStyles.toggleField}>
				<div data-thc-style-override className={formStyles.toggleInfo}>
					<span className={formStyles.label}>{`${LocalT.translate('dailyAlerts')}`}</span>
					<span className={formStyles.toggleDescription}>{`${LocalT.translate('dailyAlertsDescription')}`}</span>
				</div>
				<Toggle
					formProps={{
						input: {
							checked: isSendEmailAlert,
							name: 'edit-daily-alerts',
							onChange: onToggleEmailAlert,
							type: 'checkbox',
						},
					}}
					id="edit-daily-alerts-toggle"
					label={`${LocalT.translate('dailyAlerts')}`}
					showLabel={false}
				/>
			</div>
		</div>
	);
};
